import type { ChangeEvent, Ref } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { AvailableMint, SwapToken } from "../api/checkout";
import { mintKey } from "../api/checkout";
import type { PaymentOption } from "../utils/paymentOptions";
import {
  buildPaymentOptions,
  groupPaymentOptions,
  matchesPaymentQuery,
  paymentOptionImage,
  paymentOptionKey,
  paymentOptionLabel,
  paymentOptionSymbol,
} from "../utils/paymentOptions";
import { networkDisplayName } from "../utils/networkLabels";
import { TokenIcon } from "./TokenIcon";

export interface NetworkTokenSelectorProps {
  mints: AvailableMint[];
  /**
   * Extra tokens this checkout accepts. Empty on most checkouts, and an empty
   * list is what keeps this component rendering the plain `<select>` below.
   */
  extraTokens?: SwapToken[];
  value: PaymentOption | null;
  onChange: (option: PaymentOption) => void;
  disabled?: boolean;
  selectRef?: Ref<HTMLSelectElement>;
  /** Focus request from outside ("choose a different token"). */
  focusSignal?: number;
}

function mintLabel(mint: AvailableMint): string {
  const network = networkDisplayName(mint.chain);
  const testnet = mint.isTestnet ? " (Testnet)" : "";
  return `${network} — ${mint.token}${testnet}`;
}

/**
 * Network & token picker.
 *
 * Two renderings, one behaviour. With no extra tokens it is the original bare
 * `<select>`, byte-for-byte — most checkouts are that case, and a control that
 * changed for everyone would advertise a capability those checkouts do not
 * have. With a long list the `<select>` becomes unusable on a phone, so it is
 * replaced by a searchable, chain-grouped listbox in which every option — from
 * either source — renders through the same row template.
 */
export default function NetworkTokenSelector({
  mints,
  extraTokens = [],
  value,
  onChange,
  disabled,
  selectRef,
  focusSignal,
}: NetworkTokenSelectorProps) {
  const options = useMemo(
    () => buildPaymentOptions(mints, extraTokens),
    [mints, extraTokens],
  );

  if (extraTokens.length === 0) {
    return (
      <NativeSelect
        mints={mints}
        value={value}
        onChange={onChange}
        disabled={disabled}
        selectRef={selectRef}
      />
    );
  }

  return (
    <SearchableList
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      focusSignal={focusSignal}
    />
  );
}

/** The pre-existing control, unchanged. */
function NativeSelect({
  mints,
  value,
  onChange,
  disabled,
  selectRef,
}: {
  mints: AvailableMint[];
  value: PaymentOption | null;
  onChange: (option: PaymentOption) => void;
  disabled?: boolean;
  selectRef?: Ref<HTMLSelectElement>;
}) {
  const selectedMint = value?.kind === "mint" ? value.mint : null;
  const selectedKey = selectedMint ? mintKey(selectedMint) : "";

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const next = mints.find((mint) => mintKey(mint) === e.target.value);
    if (!next) return;
    onChange({ kind: "mint", mint: next });
  };

  return (
    <div>
      <label
        htmlFor="mint"
        className="ps-kicker"
        style={{ marginBottom: 8, display: "block" }}
      >
        Network &amp; token
      </label>
      <div className="ps-select-wrap">
        {selectedMint?.image ? (
          <img src={selectedMint.image} alt="" className="ps-select-icon" />
        ) : null}
        <select
          id="mint"
          ref={selectRef}
          className={`ps-select${selectedMint?.image ? " has-icon" : ""}`}
          value={selectedKey}
          onChange={handleChange}
          disabled={disabled || mints.length === 0}
        >
          {mints.map((mint) => (
            <option key={mintKey(mint)} value={mintKey(mint)}>
              {mintLabel(mint)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SearchableList({
  options,
  value,
  onChange,
  disabled,
  focusSignal,
}: {
  options: PaymentOption[];
  value: PaymentOption | null;
  onChange: (option: PaymentOption) => void;
  disabled?: boolean;
  focusSignal?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const filtered = useMemo(
    () => options.filter((option) => matchesPaymentQuery(option, query)),
    [options, query],
  );
  const groups = useMemo(() => groupPaymentOptions(filtered), [filtered]);
  /**
   * Keyboard navigation walks the FLATTENED GROUP order, not `filtered`.
   * `groupPaymentOptions` reorders by chain, so navigating `filtered` directly
   * would move the highlight somewhere other than where the eye expects.
   */
  const navigable = useMemo(
    () => groups.flatMap((group) => group.options),
    [groups],
  );
  const [activeIndex, setActiveIndex] = useState(0);

  // Any change to the visible set invalidates the highlight position.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  // Keep the highlighted option on screen without scrolling the page itself.
  useEffect(() => {
    if (!open) return;
    document
      .getElementById(`token-option-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  useEffect(() => {
    if (focusSignal === undefined) return;
    setOpen(true);
  }, [focusSignal]);

  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus();
    const onDocumentPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentPointerDown);
    return () =>
      document.removeEventListener("mousedown", onDocumentPointerDown);
  }, [open]);

  const select = (option: PaymentOption) => {
    onChange(option);
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  };

  const selectedKey = value ? paymentOptionKey(value) : null;
  const selectedImage = value ? paymentOptionImage(value) : null;

  return (
    <div ref={containerRef} className="ps-picker">
      <span className="ps-kicker" style={{ marginBottom: 8, display: "block" }}>
        Network &amp; token
      </span>
      <button
        type="button"
        ref={triggerRef}
        className="ps-picker-trigger"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="token-listbox"
        disabled={disabled || options.length === 0}
        onClick={() => setOpen((current) => !current)}
      >
        {value ? (
          <TokenIcon
            src={selectedImage}
            symbol={paymentOptionSymbol(value)}
            className="ps-select-icon static"
          />
        ) : null}
        <span className="ps-picker-value">
          {value ? paymentOptionLabel(value) : "Select a token"}
        </span>
        <span aria-hidden="true" className="ps-picker-caret">
          ▾
        </span>
      </button>

      {open ? (
        <div className="ps-picker-panel">
          <input
            ref={searchRef}
            className="ps-input ps-picker-search"
            type="text"
            value={query}
            placeholder="Search by token or network"
            aria-label="Search tokens"
            onChange={(e) => setQuery(e.target.value)}
            aria-controls="token-listbox"
            aria-activedescendant={
              open && navigable.length > 0
                ? `token-option-${activeIndex}`
                : undefined
            }
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setOpen(false);
                triggerRef.current?.focus();
                return;
              }
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((current) =>
                  Math.min(current + 1, navigable.length - 1),
                );
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((current) => Math.max(current - 1, 0));
                return;
              }
              if (e.key === "Enter" && navigable.length > 0) {
                e.preventDefault();
                // Selects the HIGHLIGHTED option. Previously always the first
                // match, so a keyboard user could not reach any other token.
                select(navigable[activeIndex] ?? navigable[0]);
              }
            }}
          />
          <ul id="token-listbox" role="listbox" className="ps-picker-list">
            {groups.length === 0 ? (
              <li className="ps-picker-empty">No matching tokens</li>
            ) : (
              groups.map((group) => (
                <li key={group.chainLabel}>
                  <div className="ps-picker-group">{group.chainLabel}</div>
                  <ul className="ps-picker-sublist">
                    {group.options.map((option) => {
                      const key = paymentOptionKey(option);
                      const image = paymentOptionImage(option);
                      const navIndex = navigable.indexOf(option);
                      const active = navIndex === activeIndex;
                      return (
                        <li key={key}>
                          <button
                            type="button"
                            id={`token-option-${navIndex}`}
                            role="option"
                            aria-selected={key === selectedKey}
                            className={`ps-picker-option${key === selectedKey ? " selected" : ""}${active ? " active" : ""}`}
                            onMouseEnter={() => setActiveIndex(navIndex)}
                            onClick={() => select(option)}
                          >
                            <TokenIcon
                              src={image}
                              symbol={paymentOptionSymbol(option)}
                              className="ps-select-icon static"
                            />
                            <span>{paymentOptionLabel(option)}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
